import React, { useState, useEffect } from 'react';
import * as styles from '@/components/organisms/Header/Header.css';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Button from 'components/atoms/Button/Button';
import logoImg from '@/assets/img/logo.png';
import mypageImg from '@/assets/img/mypage.png';
import { Icon } from 'icons';
import clsx from 'clsx';
import { publicAcademyApi } from '@/services/api';
import { isReservedSegment, sanitizeSlug } from '@/utils/slug';
import { useSessionSlug } from '@/hooks/useSessionSlug';



export function AcademyHeader() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userName, setUserName] = useState('');
    const [academyName, setAcademyName] = useState('');
  const [freeTrialUntil, setFreeTrialUntil] = useState<string | null>(null);
    const [logoUrl, setLogoUrl] = useState<string | null | undefined>(undefined);
    const [isScrolled, setIsScrolled] = useState(false); // 스크롤 초기화
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { slug: sessionSlug, academyName: sessionAcademyName, meta, role: sessionRole } = useSessionSlug();
    const isTeacher = (sessionRole || '').toUpperCase() === 'TEACHER';

    // 네비게이션 메뉴 데이터
    const navigationItems = [
        {
            id: 'student',
            label: '학생관리',
            path: '/academy/student/list',
            hasDropdown: true,
            dropdownItems: [
                { label: '학생관리', path: '/academy/student/list' },
                // { label: '가입신청 관리(+계정생성)', path: '/academy/student/registration' },
                { label: '트로피 및 뱃지 부여', path: '/academy/student/trophy_badge/list' }
            ]
        },
        {
            id: 'teacher',
            label: '강사관리',
            path: '/academy/teacher/list',
            hasDropdown: false
        },
        {
            id: 'word',
            label: '단어관리',
            path: '/academy/word/list',
            hasDropdown: true,
            dropdownItems: [
                { label: '단어관리', path: '/academy/word/list' },
                { label: '단어장 등록', path: '/academy/word/word_book/list' },
                { label: '단어 시험지 등록', path: '/academy/word/word_exam/list' }
            ]
        },
        {
            id: 'textbook',
            label: '교재관리',
            path: '/academy/textbook/list',
            hasDropdown: false
        },
        {
            id: 'learning',
            label: '학습 배분 및 채점',
            path: '/academy/learning/assign/list',
            hasDropdown: true,
            dropdownItems: [
                { label: '학습 및 숙제 배분', path: '/academy/learning/assign/list' },
                { label: '채점하기', path: '/academy/learning/grading/list' }
            ]
        },
        {
            id: 'paper',
            label: '온라인 시험 구매',
            path: '/academy/paper/list',
            hasDropdown: false
        },
        {
            id: 'mypage',
            label: '마이페이지',
            path: '/academy/mypage/info',
            hasDropdown: false
        },
        /* {
            id: 'playground',
            label: '플레이 그라운드 입니다',
            path: '/playground',
            hasDropdown: false
        } */
    ];
    const navigationItemsToRender = (meta.inFreeTrial
        ? navigationItems.filter((item) => item.id !== 'paper')
        : navigationItems).filter((item) => {
            if (!isTeacher) return true;
            if (item.id === 'teacher' || item.id === 'paper') return false;
            if (item.id === 'mypage') return true;
            return true;
        });

    // 토큰/표시명 초기화
    useEffect(() => {
        const token = localStorage.getItem('academy_token');
        setIsLoggedIn(!!token);
        const name = localStorage.getItem('academy_user_name') || '';
        setUserName(name);
    }, [pathname]);

    useEffect(() => {
        if (sessionAcademyName) {
            setAcademyName(sessionAcademyName);
        } else {
            const stored = localStorage.getItem('academy_name') || '';
            setAcademyName(stored);
        }
    }, [sessionAcademyName]);

    useEffect(() => {
        setFreeTrialUntil(meta.freeTrialEnd);
    }, [meta.freeTrialEnd]);

    // 스크롤 여부 상태
    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 0);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
      }, []);
    
    // 학원 로고 로딩 (slug 기반)
    useEffect(() => {
        setLogoUrl(undefined);
        const segments = pathname.split('/').filter(Boolean);
        const first = segments[0] || '';
        const slugCandidate = (!isReservedSegment(first) && first)
            ? sanitizeSlug(first)
            : sanitizeSlug(sessionSlug || '');
        if (!slugCandidate) {
            setLogoUrl(null);
            return;
        }
        publicAcademyApi
            .getByUrl(slugCandidate)
            .then((res: any) => {
                const data = res?.data || res;
                const nextLogo = data?.logoUrl || null;
                setLogoUrl(nextLogo ? String(nextLogo) : null);
            })
            .catch(() => setLogoUrl(null));
    }, [pathname, sessionSlug]);

    // /academy/ 경로가 아니면 렌더링하지 않음
    if (!pathname.includes('/academy')) {
        return null;
    }
    // slug 프리픽스 계산
    const segments = pathname.split('/').filter(Boolean);
    const first = segments[0] || '';
    const slugValue = (!isReservedSegment(first) && first) ? first : (sessionSlug || '');
    const slugPrefix = slugValue ? `/${slugValue}` : '';
  return (
    <header 
    className={clsx(
        'flex sticky top-0 justify-center mx-auto w-full border-b border-gray-100 transition-shadow z-[101] lg:static',
        isScrolled ? 'lg:!pt-0 lg:bg-white':'shadow-none'
      )}
    >
        {/* 해더 하얀부분 */}
        <div
         className={clsx(
            'flex items-center flex-col justify-between w-full bg-white transition-all lg:px-0',
            // 스크롤 안 된 상태
            !isScrolled && 'max-w-[1280px] mx-auto lg:rounded-2xl ',
            // 스크롤 된 상태
            isScrolled && 'rounded-none max-w-full'
          )}
        
        >
            <div className='flex items-center justify-between lg:px-5 w-full py-3 lg:py-6 max-w-[1280px]'>
                <Link to={isLoggedIn ? `${slugPrefix}/academy/home` : `${slugPrefix}/academy/login`} className='flex items-center gap-2 min-h-[28px] lg:min-h-[40px]'>
                {logoUrl === undefined ? (
                    <div className='w-[90px] h-[28px] lg:w-[110px] lg:h-[40px]' />
                ) : (
                    <img 
                        src={logoUrl || logoImg} 
                        alt="학원 로고" 
                        className='lg:max-h-[40px] max-h-[28px] object-cover' 
                        onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            if (target.src !== logoImg) {
                                target.src = logoImg;
                            }
                        }}
                    />
                )}
                {logoUrl !== undefined && (academyName || sessionSlug) && (
                    <span className='text-sm font-semibold text-gray-800 whitespace-nowrap'>
                        {academyName || sessionSlug}
                    </span>
                )}
                </Link>
                {/* 로그인 상태 모음 박스 */}
                <div className='hidden lg:block'>
                    {isLoggedIn ? (
                      <div className="flex gap-4 items-center cursor-pointer" onClick={() => navigate(`${slugPrefix}/academy/mypage/info`)}>
                        <img src={mypageImg} alt="아이콘" className="w-7 h-7" />
                        <div className="flex flex-col items-start">
                          <p className="text-sm">{userName || '로그인 계정'}</p>
                          <p className="text-xs text-gray-600">{academyName || '학원명'}</p>
                          {freeTrialUntil && (
                            <p className="text-xs" style={{ color: '#6366F1' }}>
                              무료체험 {String(freeTrialUntil)}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-4 items-center">
                        <Button size="small" onClick={() => navigate(`${slugPrefix}/academy/sign_up`)}>가입신청</Button>
                        <Button size="small" intent="secondary" text onClick={() => navigate(`${slugPrefix}/academy/login`)}>
                          로그인
                        </Button>
                      </div>
                    )}
                </div>
                {isLoggedIn && (
                    <div className='flex flex-col gap-0 items-end pr-3 lg:hidden'>
                        <p className='text-xs text-gray-600'>{academyName || '학원명'}</p>
                        {freeTrialUntil && (
                            <p className='text-xs font-semibold text-indigo-500'>
                                무료체험 {String(freeTrialUntil)} {/* [QA Fix] 모바일에서도 무료체험 기간 안내 */}
                            </p>
                        )}
                    </div>
                )}
                <button className='flex justify-center items-center w-10 h-10 text-gray-900 bg-white lg:hidden'>
                    <Icon name="menu" size={24} style={{ marginLeft: 8 }} />    
                </button>
            </div>
            
            <nav className='lg:flex w-full gap-4 py-2 pb-3 text-lg font-bold text-gray-900 hidden max-w-[1280px] lg:px-5'>
                {navigationItemsToRender.map((item) => (
                    <div key={item.id}>
                        {item.hasDropdown ? (
                            <div 
                                className="relative"
                                onMouseEnter={() => setActiveDropdown(item.id)}
                                onMouseLeave={() => setActiveDropdown(null)}
                            >
                                <Link className={styles.navItem + ' px-3 py-2'} to={item.path ? `${slugPrefix}${item.path}` : '#'}>
                                    {item.label}
                                </Link>
                                {activeDropdown === item.id && (
                                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[200px] z-50">
                                        {item.dropdownItems?.map((dropdownItem, index) => (
                                            <Link 
                                                key={index}
                                                to={`${slugPrefix}${dropdownItem.path}`} 
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                            >
                                                {dropdownItem.label}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Link to={item.path ? `${slugPrefix}${item.path}` : '#'} className={styles.navItem + ' px-3 py-2'}>
                                {item.label}
                            </Link>
                        )}
                    </div>
                ))}
            </nav>
        </div>
    </header>
  );
}